import { ref } from 'vue'
import { forgotPasswordService } from '@/services/authServices'
import { useToast } from 'vue-toastification'

export function useForgotPassword() {
	const toast = useToast()
	const toastTimeout = 5000

	const email = ref('')
	const otp = ref('')
	const newPassword = ref('')
	const confirmPassword = ref('')
	const step = ref(1)
	const loading = ref(false)
	const error = ref('')

	const timeLeft = ref(0)
	let timer: ReturnType<typeof setInterval> | null = null

	const startTimer = () => {
		timeLeft.value = 300 // 5 minutes in seconds
		if (timer) clearInterval(timer)
		timer = setInterval(() => {
			if (timeLeft.value > 0) {
				timeLeft.value--
			} else {
				if (timer) clearInterval(timer)
			}
		}, 1000)
	}

	const sendOtp = async () => {
		loading.value = true
		error.value = ''
		try {
			await forgotPasswordService.sendOtp(email.value)
			step.value = 2
			startTimer()
			toast.success('OTP sent to your email', { timeout: toastTimeout })
		} catch (err: any) {
			error.value = err.response?.data?.message || 'Failed to send OTP'
			toast.error(error.value, { timeout: toastTimeout })
		} finally {
			loading.value = false
		}
	}

	const verifyOtp = async () => {
		loading.value = true
		error.value = ''
		try {
			await forgotPasswordService.verifyOtp(email.value, otp.value)
			step.value = 3
			toast.success('OTP verified. Please enter your new password.', { timeout: toastTimeout })
		} catch (err: any) {
			error.value = err.response?.data?.message || 'Invalid OTP'
			toast.error(error.value, { timeout: toastTimeout })
		} finally {
			loading.value = false
		}
	}

	const changePassword = async () => {
		if (newPassword.value !== confirmPassword.value) {
			error.value = 'Passwords do not match'
			toast.error(error.value, { timeout: toastTimeout })
			return
		}

		loading.value = true
		error.value = ''
		try {
			await forgotPasswordService.resetPassword(
				email.value,
				otp.value,
				newPassword.value,
				confirmPassword.value
			)
			step.value = 4
			toast.success('Password reset successful. You can now log in with your new password.', { timeout: toastTimeout })
		} catch (err: any) {
			error.value = err.response?.data?.message || 'Failed to reset password'
			toast.error(error.value, { timeout: toastTimeout })
		} finally {
			loading.value = false
		}
	}

	return {
		email,
		otp,
		newPassword,
		confirmPassword,
		step,
		loading,
		error,
		timeLeft,
		sendOtp,
		verifyOtp,
		changePassword,
	}
}
